import React, { useState, useMemo } from 'react';
import { SearchIcon, PencilIcon, BarChart3Icon, MailIcon, MessageSquareIcon, PhoneIcon, BellIcon, GlobeIcon, Share2Icon, UsersIcon, TargetIcon, MapPinIcon, FilterIcon, InboxIcon, MonitorIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { getCategoryIcon, getCategoryLabel } from '../data/communications';
import type { ChannelKey, Notice, NoticeStatus } from '../types/notice';
const CHANNEL_ICONS: Record<ChannelKey, typeof MailIcon> = {
  email: MailIcon,
  sms: MessageSquareIcon,
  voice: PhoneIcon,
  push: BellIcon,
  website: GlobeIcon,
  social: Share2Icon,
  screens: MonitorIcon
};
const AUDIENCE_ICONS = {
  org: UsersIcon,
  sector: TargetIcon,
  location: MapPinIcon,
  filter: FilterIcon
};
interface NoticeListProps {
  notices: Notice[];
  onCreate: () => void;
  onEdit: (notice: Notice) => void;
  onViewStats: (notice: Notice) => void;
}
export function NoticeList({
  notices,
  onCreate,
  onEdit,
  onViewStats
}: NoticeListProps) {
  const {
    t,
    lang
  } = useLanguage();
  const [status, setStatus] = useState<NoticeStatus | 'all'>('all');
  const [query, setQuery] = useState('');
  const locale = lang === 'fr' ? 'fr-CA' : 'en-CA';
  const statusMeta: Record<NoticeStatus, {
    label: string;
    className: string;
    dot: string;
  }> = {
    sent: {
      label: t('Envoyé', 'Sent'),
      className: 'bg-emerald-50 text-emerald-700 border-emerald-200',
      dot: 'bg-emerald-500'
    },
    scheduled: {
      label: t('Programmé', 'Scheduled'),
      className: 'bg-amber-50 text-amber-700 border-amber-200',
      dot: 'bg-amber-500'
    },
    draft: {
      label: t('Brouillon', 'Draft'),
      className: 'bg-slate-100 text-slate-600 border-slate-200',
      dot: 'bg-slate-400'
    }
  };

  /** Day only in the list; the exact time is kept for the hover title. */
  const dayOf = (iso: string) => new Date(iso).toLocaleDateString(locale, {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  });
  const timeOf = (iso: string) => new Date(iso).toLocaleTimeString(locale, {
    hour: '2-digit',
    minute: '2-digit'
  });
  const filters: {
    id: NoticeStatus | 'all';
    label: string;
  }[] = [{
    id: 'all',
    label: t('Tous', 'All')
  }, {
    id: 'sent',
    label: t('Envoyés', 'Sent')
  }, {
    id: 'scheduled',
    label: t('Programmés', 'Scheduled')
  }, {
    id: 'draft',
    label: t('Brouillons', 'Drafts')
  }];
  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return notices.filter(n => status === 'all' ? true : n.status === status).filter(n => q ? n.title.toLowerCase().includes(q) || getCategoryLabel(n.category)[lang].toLowerCase().includes(q) : true).sort((a, b) => +new Date(b.date) - +new Date(a.date));
  }, [notices, status, query, lang]);
  const countFor = (id: NoticeStatus | 'all') => id === 'all' ? notices.length : notices.filter(n => n.status === id).length;
  return <div className="space-y-6 animate-in fade-in duration-300" data-id="element-4173" data-mp="25davakmkzhbc">
      {/* Toolbar */}
      <div className="flex flex-col lg:flex-row lg:items-center gap-4 justify-between" data-id="element-4174" data-mp="2acnjb8p6uufi">
        <div className="flex flex-wrap items-center gap-2" data-id="element-4175" data-mp="2f5p1pqoltc3u">
          {filters.map(f => {
          const active = status === f.id;
          return <button key={f.id} onClick={() => setStatus(f.id)} className={`px-3.5 py-2 rounded-lg text-sm font-medium transition-colors border ${active ? 'bg-[#0F172A] text-white border-[#0F172A]' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'}`} data-id="element-4176" data-mp="1mh18n8xm5ifq">
                {f.label}
                <span className={`ml-2 text-xs ${active ? 'text-slate-300' : 'text-slate-400'}`} data-id="element-4177" data-mp="37t2q30e8kzcc">
                  {countFor(f.id)}
                </span>
              </button>;
        })}
        </div>

        <div className="flex items-center gap-3" data-id="element-4178" data-mp="36cp00ddkq2az">
          <div className="relative flex-1 lg:w-72" data-id="element-4179" data-mp="36e9rtv8i7755">
            <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" data-id="element-4180" data-mp="3kc6sb2q2vxp3" />
            <input type="search" value={query} onChange={e => setQuery(e.target.value)} placeholder={t('Rechercher un avis…', 'Search notices…')} aria-label={t('Rechercher un avis', 'Search notices')} className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-[#4F46E5] focus:border-[#4F46E5]" data-id="element-4181" data-mp="3fd3t3yws4lp1" />
          </div>
          <button onClick={onCreate} className="shrink-0 inline-flex items-center gap-2 px-4 py-2 bg-[#4F46E5] hover:bg-[#4338CA] text-white text-sm font-semibold rounded-lg transition-colors shadow-sm" data-id="element-4182" data-mp="gvrc55fgcx5n">
            {t('Créer un avis', 'Create a notice')}
          </button>
        </div>
      </div>

      {/* List */}
      {visible.length === 0 ? <div className="bg-white rounded-xl border border-slate-200 py-16 px-6 text-center" data-id="element-4183" data-mp="zhpbmtvc84g1">
          <div className="w-12 h-12 mx-auto rounded-full bg-slate-100 flex items-center justify-center mb-3" data-id="element-4184" data-mp="lmmwrh8ij4r3">
            <InboxIcon className="w-5 h-5 text-slate-400" data-id="element-4185" data-mp="buh8oildt7h6" />
          </div>
          <p className="text-sm font-medium text-slate-600" data-id="element-4186" data-mp="bsct33e4ek12">
            {t('Aucun avis ne correspond à ces critères.', 'No notice matches these criteria.')}
          </p>
          <p className="text-xs text-slate-400 mt-1" data-id="element-4187" data-mp="2v2gnepkgouk">
            {t('Ajustez la recherche ou créez un nouvel avis.', 'Adjust your search or create a new notice.')}
          </p>
        </div> : <ul className="space-y-2" data-id="element-4188" data-mp="3u02kxuzjti4a">
          {visible.map(notice => {
        const meta = statusMeta[notice.status];
        const AudienceIcon = AUDIENCE_ICONS[notice.audience.kind];
        const CategoryIcon = getCategoryIcon(notice.category);
        return <li key={notice.id} onDoubleClick={() => onEdit(notice)} className="group bg-white rounded-xl border border-slate-200 px-5 py-4 hover:border-[#4F46E5]/40 hover:shadow-sm transition-all" data-id="element-4189" data-mp="362nuw94yw4cv">
                <div className="flex items-center gap-4" data-id="element-4190" data-mp="3tt0bs9i6xbfl">
                  {/* Status + category icon on one line, channels underneath. */}
                  <div className="hidden sm:flex flex-col gap-1.5 w-40 shrink-0" data-id="element-4191" data-mp="3otdzq3e3m18d">
                    <div className="flex items-center gap-2" data-id="element-4192" data-mp="3jy8df3gsakjr">
                      <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium border w-fit ${meta.className}`} data-id="element-4193" data-mp="3ew0wdzh3keak">
                        <span className={`w-1.5 h-1.5 rounded-full ${meta.dot}`} data-id="element-4194" data-mp="23gubild6snpl" />
                        {meta.label}
                      </span>
                      <span title={getCategoryLabel(notice.category)[lang]} aria-label={getCategoryLabel(notice.category)[lang]} className="inline-flex items-center justify-center w-6 h-6 rounded-md bg-slate-50 border border-slate-200 text-slate-500" data-id="element-4195" data-mp="iy0cripikld1">
                        <CategoryIcon className="w-3.5 h-3.5" aria-hidden="true" data-id="element-4196" data-mp="12msmh8cspdq1" />
                      </span>
                    </div>
                    <span className="inline-flex items-center gap-1.5" data-id="element-4197" data-mp="16rihyw3jkqaf">
                      {notice.channels.map(c => {
                  const Icon = CHANNEL_ICONS[c];
                  return <Icon key={c} className="w-3.5 h-3.5 text-slate-400" aria-hidden="true" data-id="element-4198" data-mp="1gl92xr0gmild" />;
                })}
                      <span className="sr-only" data-id="element-4199" data-mp="1o5te5eiupeyw">
                        {notice.channels.length} {t('canaux', 'channels')}
                      </span>
                    </span>
                  </div>

                  <div className="min-w-0 flex-1" data-id="element-4200" data-mp="1xwebyzqpafgr">
                    <h3 className="text-base font-semibold text-[#0F172A] truncate" data-id="element-4201" data-mp="1sy4geo12dcum">
                      {notice.title}
                    </h3>

                    <div className="flex items-center gap-3 flex-wrap mt-1 text-xs text-slate-500" data-id="element-4202" data-mp="158s8rsldxcuf">
                      {notice.status !== 'draft' && <>
                          <span data-id="element-4203" data-mp="1f7hjn1ev5x16">
                            {notice.endDate ? <>
                                {t('Du ', 'From ')}
                                <span className="font-medium text-slate-700" title={timeOf(notice.date)} data-id="element-4204" data-mp="2ql3g77n1z0wq">
                                  {dayOf(notice.date)}
                                </span>
                                {t(' au ', ' to ')}
                                <span className="font-medium text-slate-700" title={timeOf(notice.endDate)} data-id="element-4205" data-mp="2y6ffzm0miur1">
                                  {dayOf(notice.endDate)}
                                </span>
                              </> : <>
                                {notice.status === 'scheduled' ? t('Envoi prévu le ', 'Sending on ') : t('Envoyé le ', 'Sent on ')}
                                <span className="font-medium text-slate-700" title={timeOf(notice.date)} data-id="element-4206" data-mp="373pnqxe8g1r7">
                                  {dayOf(notice.date)}
                                </span>
                              </>}
                          </span>
                          <span aria-hidden="true" className="text-slate-300" data-id="element-4207" data-mp="hqrm7zj5rnhx">
                            ·
                          </span>
                        </>}
                      <span className="inline-flex items-center gap-1.5" data-id="element-4208" data-mp="7rimeq1m3enn">
                        <AudienceIcon className="w-3.5 h-3.5 text-slate-400" data-id="element-4209" data-mp="3q6bccpoj8psr" />
                        <span className="font-medium text-slate-700" data-id="element-4210" data-mp="3uyl1w4lqjnui">
                          {notice.audience.count.toLocaleString(locale)}
                        </span>
                        <span className="hidden md:inline" data-id="element-4211" data-mp="3l89kbf94pg55">
                          {t('citoyens', 'citizens')}
                        </span>
                      </span>
                    </div>
                  </div>

                  {/* Revealed on hover — kept reachable by keyboard at all times. */}
                  <div className="flex items-center gap-1 shrink-0 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity" data-id="element-4212" data-mp="2sli6u29owtos">
                    <button onClick={() => onEdit(notice)} aria-label={t(`Modifier « ${notice.title} »`, `Edit “${notice.title}”`)} title={t('Modifier', 'Edit')} className="p-2 rounded-lg text-slate-500 hover:text-[#4F46E5] hover:bg-indigo-50 focus:outline-none focus:ring-2 focus:ring-[#4F46E5] transition-colors" data-id="element-4213" data-mp="2no9nvzgpwlmh">
                      <PencilIcon className="w-4 h-4" data-id="element-4214" data-mp="3kcfyd30bm88o" />
                    </button>
                    <button onClick={() => onViewStats(notice)} aria-label={t(`Statistiques de « ${notice.title} »`, `Statistics for “${notice.title}”`)} title={t('Statistiques', 'Statistics')} className="p-2 rounded-lg text-slate-500 hover:text-[#4F46E5] hover:bg-indigo-50 focus:outline-none focus:ring-2 focus:ring-[#4F46E5] transition-colors" data-id="element-4215" data-mp="3gcf9spjicgnw">
                      <BarChart3Icon className="w-4 h-4" data-id="element-4216" data-mp="2vxztrhrr4wt5" />
                    </button>
                  </div>
                </div>
              </li>;
      })}
        </ul>}
    </div>;
}